// src/pages/Chapters.tsx
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Box, Typography, Button, IconButton, Tooltip, Dialog, DialogContent,
    DialogActions, TextField, FormControl, InputLabel, Select, MenuItem,
    CircularProgress, Stack, Snackbar, Alert, Paper, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Chip, InputAdornment,
} from "@mui/material";
import {
    Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon,
    Article as ResourceIcon, Clear as ClearIcon, Search as SearchIcon,
    MenuBook as ChapterIcon, FilterList as FilterIcon,
} from "@mui/icons-material";
import { keyframes } from "@mui/system";

import { getClasses, getMediums, getSubjects } from "../services/subjectService";
import { getChapters, createChapter, updateChapter, deleteChapter } from "../services/chapterService";
import { TableShimmer } from "../components/Shimmer";

const PRIMARY = "#174143";
const ACCENT = "#3d9ea4";

const fadeUp = keyframes`
  from { opacity:0; transform:translateY(16px); }
  to   { opacity:1; transform:translateY(0); }
`;

interface Chapter {
    id: number;
    name: string;
    displayOrder?: number | null;
    description?: string | null;
    subject?: { id: number; name: string; schoolClass?: { id: number; name: string } };
}

const chapterCache: Record<string, Chapter[]> = {};

export default function ChaptersPage(): React.ReactElement {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [classes, setClasses] = useState<any[]>([]);
    const [mediums, setMediums] = useState<any[]>([]);
    const [subjects, setSubjects] = useState<any[]>([]);
    const [chapters, setChapters] = useState<Chapter[]>([]);
    const [chapterSearch, setChapterSearch] = useState("");

    const [loading, setLoading] = useState(false);
    const [loadingMeta, setLoadingMeta] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [openEditor, setOpenEditor] = useState(false);
    const [editing, setEditing] = useState<Chapter | null>(null);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ title: "", subjectId: "", order: "", description: "" });
    const [titleError, setTitleError] = useState("");

    const [snackbar, setSnackbar] = useState<{ severity: "success" | "error"; message: string } | null>(null);

    // ── URL filter state ─────────────────────────────────
    const selectedClassId = searchParams.get("classId") || "";
    const selectedMediumId = searchParams.get("mediumId") || "";
    const selectedSubjectId = searchParams.get("subjectId") || "";

    const updateFilter = (key: string, value: string) => {
        const params = new URLSearchParams(searchParams.toString());
        if (value) params.set(key, value); else params.delete(key);
        if (key === "classId" || key === "mediumId") params.delete("subjectId");
        setSearchParams(params);
    };
    const clearFilters = () => setSearchParams({});

    // ── Metadata ──────────────────────────────────────────
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                setLoadingMeta(true);
                const [cls, md] = await Promise.all([getClasses(), getMediums()]);
                if (mounted) {
                    setClasses(Array.isArray(cls) ? cls : []);
                    setMediums(Array.isArray(md) ? md : []);
                }
            } catch (e) { console.error("meta failed", e); }
            finally { if (mounted) setLoadingMeta(false); }
        })();
        return () => { mounted = false; };
    }, []);

    // ── Load subjects on class/medium change ──────────────
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const subs = await getSubjects({
                    classId: selectedClassId ? Number(selectedClassId) : undefined,
                    mediumId: selectedMediumId ? Number(selectedMediumId) : undefined,
                });
                if (mounted) setSubjects(Array.isArray(subs) ? subs : []);
            } catch { if (mounted) setSubjects([]); }
        })();
        return () => { mounted = false; };
    }, [selectedClassId, selectedMediumId]);

    // ── Load chapters on subject change ───────────────────
    const loadChapters = useCallback(async () => {
        if (!selectedSubjectId) { setChapters([]); return; }
        if (chapterCache[selectedSubjectId]) { setChapters(chapterCache[selectedSubjectId]); return; }
        setLoading(true); setError(null);
        try {
            const list = await getChapters({ subjectId: Number(selectedSubjectId), page: 0, size: 200 });
            const norm = Array.isArray(list) ? list : ((list as any).content ?? []);
            chapterCache[selectedSubjectId] = norm;
            setChapters(norm);
        } catch (err: any) {
            setError(err?.message || "Failed to load chapters");
            setChapters([]);
        } finally { setLoading(false); }
    }, [selectedSubjectId]);

    useEffect(() => { loadChapters(); }, [loadChapters]);

    // ── Filtered chapter list by search ───────────────────
    const filteredChapters = chapterSearch.trim()
        ? chapters.filter(ch => ch.name.toLowerCase().includes(chapterSearch.toLowerCase()))
        : chapters;

    // ── Handlers ──────────────────────────────────────────
    const handleOpenCreate = () => {
        setEditing(null);
        setForm({ title: "", subjectId: selectedSubjectId || "", order: "", description: "" });
        setTitleError("");
        setOpenEditor(true);
    };
    const handleOpenEdit = (ch: Chapter) => {
        setEditing(ch);
        setForm({
            title: ch.name,
            subjectId: String(selectedSubjectId || ""),
            order: ch.displayOrder != null ? String(ch.displayOrder) : "",
            description: ch.description || "",
        });
        setTitleError("");
        setOpenEditor(true);
    };
    const handleSubmit = async () => {
        if (!form.title.trim()) { setTitleError("Chapter name is required"); return; }
        if (!form.subjectId) { setSnackbar({ severity: "error", message: "Subject is required" }); return; }
        setSaving(true);
        const payload = {
            subjectId: Number(form.subjectId),
            name: form.title.trim(),
            displayOrder: form.order ? Number(form.order) : 1,
            description: form.description.trim() || null,
        };
        try {
            if (editing) await updateChapter(editing.id, payload);
            else await createChapter(payload);

            const key = String(payload.subjectId);
            if (chapterCache[key]) delete chapterCache[key];

            setSnackbar({ severity: "success", message: editing ? "Chapter updated" : "Chapter created" });
            setOpenEditor(false);
            if (String(payload.subjectId) === selectedSubjectId) loadChapters();
        } catch (err: any) {
            setSnackbar({ severity: "error", message: err?.message || "Operation failed" });
        } finally { setSaving(false); }
    };
    const handleDelete = async (id: number) => {
        if (!window.confirm("Delete this chapter permanently?")) return;
        try {
            await deleteChapter(id);
            if (selectedSubjectId && chapterCache[selectedSubjectId]) delete chapterCache[selectedSubjectId];
            setSnackbar({ severity: "success", message: "Chapter deleted" });
            loadChapters();
        } catch (err: any) {
            setSnackbar({ severity: "error", message: err?.message || "Delete failed" });
        }
    };
    const handleViewResources = (ch: Chapter) => {
        navigate(`/chapters/resources/${ch.id}`, { state: { name: ch.name, description: ch.description } });
    };

    const selectedSubjectName = subjects.find(s => String(s.id) === selectedSubjectId)?.name;
    const hasFilters = !!(selectedClassId || selectedMediumId || selectedSubjectId);

    // ── Render ────────────────────────────────────────────
    return (
        <Box sx={{ minHeight: "100vh", background: "#f5f6fa", p: { xs: 2, md: 3 } }}>
            <Box sx={{ maxWidth: 1400, mx: "auto" }}>

                {/* ── Hero Header ── */}
                <Paper elevation={0} sx={{
                    mb: 3, borderRadius: 4, overflow: "hidden",
                    background: `linear-gradient(135deg, ${PRIMARY} 0%, #2a6b6f 60%, ${ACCENT} 100%)`,
                    color: "#fff", animation: `${fadeUp} 0.4s ease-out`, position: "relative",
                }}>
                    <Box sx={{ position: "absolute", top: -50, right: -30, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.05)", pointerEvents: "none" }} />
                    <Box sx={{ position: "absolute", bottom: -20, right: 120, width: 80, height: 80, borderRadius: "50%", background: "rgba(255,255,255,0.07)", pointerEvents: "none" }} />

                    <Box sx={{ p: { xs: 3, md: 4 }, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2, position: "relative" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Box sx={{ width: 52, height: 52, borderRadius: 2.5, background: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <ChapterIcon sx={{ fontSize: 28 }} />
                            </Box>
                            <Box>
                                <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: -0.5 }}>Chapter Management</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.75, mt: 0.3 }}>
                                    {selectedSubjectName
                                        ? <>Chapters for <strong>{selectedSubjectName}</strong></>
                                        : "Select a class, medium and subject to manage chapters"}
                                </Typography>
                            </Box>
                        </Box>

                        <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="center">
                            {selectedSubjectId && (
                                <Chip label={`${chapters.length} Chapters`} sx={{ background: "rgba(255,255,255,0.18)", color: "#fff", fontWeight: 700 }} />
                            )}
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={handleOpenCreate}
                                disabled={!selectedSubjectId}
                                sx={{ background: "rgba(255,255,255,0.2)", color: "#fff", fontWeight: 700, borderRadius: 2, backdropFilter: "blur(4px)", border: "1px solid rgba(255,255,255,0.3)", "&:hover": { background: "rgba(255,255,255,0.3)" }, "&:disabled": { opacity: 0.4, color: "#fff" } }}
                            >
                                New Chapter
                            </Button>
                        </Stack>
                    </Box>
                </Paper>

                {/* ── Filter Bar ── */}
                <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", p: 2, mb: 2.5, animation: `${fadeUp} 0.4s ease-out 60ms both` }}>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: "text.secondary", minWidth: 80 }}>
                            <FilterIcon fontSize="small" />
                            <Typography variant="body2" fontWeight={700}>Filters</Typography>
                        </Box>

                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel>Class</InputLabel>
                            <Select value={selectedClassId} label="Class" onChange={e => updateFilter("classId", String(e.target.value))}>
                                <MenuItem value=""><em>All Classes</em></MenuItem>
                                {classes.map(c => <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>)}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel>Medium</InputLabel>
                            <Select value={selectedMediumId} label="Medium" onChange={e => updateFilter("mediumId", String(e.target.value))}>
                                <MenuItem value=""><em>All Mediums</em></MenuItem>
                                {mediums.map(m => <MenuItem key={m.id} value={String(m.id)}>{m.name}</MenuItem>)}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel>Subject *</InputLabel>
                            <Select value={selectedSubjectId} label="Subject *" onChange={e => updateFilter("subjectId", String(e.target.value))}>
                                <MenuItem value=""><em>Select Subject</em></MenuItem>
                                {subjects.map(s => <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>)}
                            </Select>
                        </FormControl>

                        {hasFilters && (
                            <Tooltip title="Clear all filters">
                                <IconButton size="small" onClick={clearFilters} sx={{ color: "#ef4444", border: "1px solid rgba(239,68,68,0.3)", "&:hover": { background: "rgba(239,68,68,0.08)" } }}>
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}

                        <Box sx={{ flex: 1 }} />

                        {/* Chapter search — only when subject selected */}
                        {selectedSubjectId && (
                            <TextField
                                size="small"
                                placeholder="Search chapters…"
                                value={chapterSearch}
                                onChange={e => setChapterSearch(e.target.value)}
                                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: "text.disabled" }} /></InputAdornment> }}
                                sx={{ minWidth: 200 }}
                            />
                        )}
                    </Box>
                </Paper>

                {/* ── Content ── */}
                {loadingMeta ? (
                    <TableShimmer rows={6} cols={5} />
                ) : (
                    <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", overflow: "hidden", animation: `${fadeUp} 0.4s ease-out 120ms both` }}>
                        {loading ? (
                            <TableShimmer rows={6} cols={5} />
                        ) : error ? (
                            <Box sx={{ py: 8, textAlign: "center" }}>
                                <Typography color="error">⚠ {error}</Typography>
                            </Box>
                        ) : !selectedSubjectId ? (
                            <Box sx={{ py: 10, textAlign: "center" }}>
                                <ChapterIcon sx={{ fontSize: 56, color: "text.disabled", mb: 2 }} />
                                <Typography variant="h6" color="text.secondary" fontWeight={700}>No Subject Selected</Typography>
                                <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
                                    Use the filters above to select a Class → Medium → Subject
                                </Typography>
                            </Box>
                        ) : filteredChapters.length === 0 ? (
                            <Box sx={{ py: 10, textAlign: "center" }}>
                                <ChapterIcon sx={{ fontSize: 56, color: "text.disabled", mb: 2 }} />
                                <Typography variant="h6" color="text.secondary" fontWeight={700}>
                                    {chapterSearch ? "No chapters match your search" : "No chapters yet"}
                                </Typography>
                                {!chapterSearch && (
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        onClick={handleOpenCreate}
                                        sx={{ mt: 2, background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`, fontWeight: 700, borderRadius: 2 }}
                                    >
                                        Create First Chapter
                                    </Button>
                                )}
                            </Box>
                        ) : (
                            <TableContainer sx={{ maxHeight: "62vh" }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            {["Order", "Chapter Name", "Description", "Actions"].map((h, i) => (
                                                <TableCell key={i} align={i === 3 ? "right" : "left"}
                                                    sx={{ fontWeight: 700, fontSize: "0.72rem", letterSpacing: 0.5, textTransform: "uppercase", color: "text.secondary", background: "#f8fafc", py: 1.5 }}
                                                >{h}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredChapters.map((ch) => (
                                            <TableRow key={ch.id} hover sx={{ "&:hover": { background: "#f0fafa" }, transition: "background 0.15s" }}>
                                                <TableCell sx={{ width: 80 }}>
                                                    <Box sx={{
                                                        width: 32, height: 32, borderRadius: 1.5,
                                                        background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`,
                                                        color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                                                        fontWeight: 800, fontSize: "0.8rem",
                                                    }}>
                                                        {ch.displayOrder ?? "—"}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={700} color={PRIMARY}>{ch.name}</Typography>
                                                    <Typography variant="caption" color="text.disabled">ID: {ch.id}</Typography>
                                                </TableCell>
                                                <TableCell sx={{ maxWidth: 340 }}>
                                                    <Typography variant="body2" color="text.secondary" noWrap>
                                                        {ch.description || <em style={{ opacity: 0.4 }}>No description</em>}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                        <Tooltip title="View Resources">
                                                            <IconButton size="small" onClick={() => handleViewResources(ch)}
                                                                sx={{ color: ACCENT, "&:hover": { background: "rgba(61,158,164,0.1)" } }}>
                                                                <ResourceIcon sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Edit">
                                                            <IconButton size="small" onClick={() => handleOpenEdit(ch)}
                                                                sx={{ color: PRIMARY, "&:hover": { background: "rgba(23,65,67,0.08)" } }}>
                                                                <EditIcon sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete">
                                                            <IconButton size="small" onClick={() => handleDelete(ch.id)}
                                                                sx={{ color: "#ef4444", "&:hover": { background: "rgba(239,68,68,0.08)" } }}>
                                                                <DeleteIcon sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Paper>
                )}
            </Box>

            {/* ── Create / Edit Dialog ── */}
            <Dialog open={openEditor} onClose={() => setOpenEditor(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <Box sx={{ background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`, px: 3, py: 2.5, color: "#fff" }}>
                    <Typography variant="h6" fontWeight={800}>{editing ? "Edit Chapter" : "New Chapter"}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.75 }}>
                        {editing ? "Update chapter details below." : "Fill in the details to create a new chapter."}
                    </Typography>
                </Box>

                <DialogContent dividers sx={{ pt: 2.5 }}>
                    <Stack spacing={2.5}>
                        <TextField
                            label="Chapter Name *"
                            value={form.title}
                            onChange={e => { setForm({ ...form, title: e.target.value }); setTitleError(""); }}
                            fullWidth size="small"
                            error={!!titleError}
                            helperText={titleError}
                            autoFocus
                            onKeyDown={e => { if (e.key === "Enter") handleSubmit(); }}
                        />
                        <TextField
                            label="Display Order"
                            type="number"
                            value={form.order}
                            onChange={e => setForm({ ...form, order: e.target.value })}
                            fullWidth size="small"
                            helperText="Sort order in the app (e.g. 1, 2, 3)"
                            inputProps={{ min: 1 }}
                        />
                        <TextField
                            label="Description"
                            value={form.description}
                            onChange={e => setForm({ ...form, description: e.target.value })}
                            multiline rows={3}
                            fullWidth size="small"
                            placeholder="Optional — brief description of this chapter"
                        />
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setOpenEditor(false)} color="inherit">Cancel</Button>
                    <Button variant="contained" onClick={handleSubmit} disabled={saving}
                        sx={{ background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`, fontWeight: 700, px: 3, borderRadius: 2 }}>
                        {saving ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : (editing ? "Update Chapter" : "Create Chapter")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Snackbar ── */}
            <Snackbar open={!!snackbar} autoHideDuration={4000} onClose={() => setSnackbar(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
                <Alert onClose={() => setSnackbar(null)} severity={snackbar?.severity ?? "info"} variant="filled" sx={{ borderRadius: 2 }}>
                    {snackbar?.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}